import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  fetchRecommendedSendTime,
  isAdHocTemporaryOutboxDraft,
  isOutboxStepEligibleForRecommendedSendTime,
} from '@/lib/outboxRecommendedSendTime';

const CONFIDENCE_STYLES = {
  high: { dot: 'bg-emerald-500', label: 'High confidence' },
  medium: { dot: 'bg-amber-500', label: 'Medium confidence' },
  low: { dot: 'bg-gray-400', label: 'Low confidence' },
};

function confidenceMeta(confidence) {
  const key = String(confidence || '').toLowerCase();
  return CONFIDENCE_STYLES[key] || CONFIDENCE_STYLES.low;
}

function tzShort(tz) {
  if (!tz) return '';
  const seg = String(tz).split('/').pop() || String(tz);
  return seg.replace(/_/g, ' ');
}

export function RecommendedSendTimeCardSkeleton({ className }) {
  return (
    <div
      className={cn(
        'rounded-md border border-gray-200 bg-gray-50/80 px-4 py-3 mb-2',
        className,
      )}
      aria-busy="true"
      aria-label="Loading recommended send time"
    >
      <Skeleton className="h-4 w-full max-w-[280px]" />
    </div>
  );
}

/**
 * Fetches and displays optimal send time for email scheduling (playbook + ad-hoc).
 * @param {{ draft: object, tenantId: string, userId?: string, onApply: (iso: string) => void, className?: string }} props
 */
export default function RecommendedSendTimeCard({
  draft,
  tenantId,
  userId,
  onApply,
  className,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  const stepId = draft?.stepId || draft?.step_id || draft?.id;
  const personId =
    draft?.persona_id ??
    draft?.person_id ??
    draft?.personId ??
    draft?.person?.persona_id ??
    draft?.person?.person_id ??
    draft?.person?.id;
  const accountId =
    draft?.account_id ?? draft?.accountId ?? draft?.account?.id ?? draft?.account?.account_id;
  const isTemporary = isAdHocTemporaryOutboxDraft({
    stepId,
    isTemporary: draft?.is_temporary,
  });
  const isSent = draft?.status === 'sent';
  const isHistoryItem =
    draft?.is_history || draft?.status === 'manual_completion';
  const eligible = isOutboxStepEligibleForRecommendedSendTime({
    stepId,
    isSent,
    isHistoryItem,
    channel: draft?.channel,
  });

  useEffect(() => {
    if (!eligible || !tenantId || !stepId) {
      setData(null);
      setLoading(false);
      return undefined;
    }

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setData(null);

    fetchRecommendedSendTime({
      stepId,
      tenantId,
      userId,
      personaId: personId,
      accountId,
      isTemporary,
      signal: ctrl.signal,
    })
      .then((result) => {
        if (!ctrl.signal.aborted) setData(result);
      })
      .catch(() => {})
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });

    return () => ctrl.abort();
  }, [stepId, tenantId, userId, personId, accountId, isTemporary, eligible]);

  if (!eligible) return null;
  if (loading) return <RecommendedSendTimeCardSkeleton className={className} />;
  if (!data?.recommended_send_at) return null;

  const label = String(data.local_time_label || '').trim() || 'Recommended time';
  const timezone = String(data.timezone || '').trim();
  const recipientTimeLabel = String(data.recipient_time_label || '').trim();
  const recipientTimezone = String(data.recipient_timezone || '').trim();
  const reason = String(data.reason || '').trim();
  const isRecipientLocal = data.model === 'recipient_local' && !!recipientTimeLabel;
  const headline = isRecipientLocal ? recipientTimeLabel : label;
  const headlineTz = isRecipientLocal ? tzShort(recipientTimezone) : '';
  const senderTzShort = tzShort(timezone);
  const showOutsideWindowPill = isRecipientLocal && data.outside_sender_window === true;
  const statsFresh = data.stats_fresh !== false;
  const pct =
    statsFresh && Number.isFinite(Number(data.expected_open_rate))
      ? Math.round(Number(data.expected_open_rate) * 100)
      : null;
  const conf = statsFresh ? confidenceMeta(data.confidence) : null;

  return (
    <div
      className={cn(
        'rounded-md border border-[#00C2C2] bg-[#ECFFFE] p-3 mb-2 flex items-start gap-3',
        className,
      )}
    >
      <Sparkles className="w-4 h-4 text-[#00C2C2] shrink-0 mt-0.5" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-[#0A1A2F]">
                Recommended: {headline}
                {headlineTz ? ` (${headlineTz})` : ''}
              </span>
              {statsFresh && conf ? (
                <span
                  className={cn('w-2 h-2 rounded-full shrink-0', conf.dot)}
                  role="img"
                  aria-label={conf.label}
                />
              ) : null}
            </div>
            {isRecipientLocal ? (
              <div className="text-[11px] text-gray-500 mt-0.5">
                ↳ {label} your time{senderTzShort ? ` (${senderTzShort})` : ''}
              </div>
            ) : timezone ? (
              <div className="text-[11px] text-gray-500 mt-0.5">{timezone}</div>
            ) : null}
            {showOutsideWindowPill ? (
              <div
                title="This lands during the recipient's peak open hours, which falls outside your configured send window. The message will still send at this time if you Apply."
                className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium border border-amber-200"
              >
                🌙 Outside your usual hours — recipient-local optimum
              </div>
            ) : null}
            {pct != null ? (
              <div className="text-xs text-gray-600 mt-1">{pct}% expected true-open rate</div>
            ) : (
              <div className="text-xs text-gray-500 mt-1 italic">
                Personalised once we have more send data.
              </div>
            )}
          </div>
          {reason ? (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 p-1 rounded-sm text-gray-400 hover:text-gray-600 hover:bg-white/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2]"
                  aria-label="Why this time is recommended"
                >
                  <Info className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="max-w-sm text-xs leading-relaxed z-[70]"
                align="end"
                side="bottom"
              >
                {reason}
              </PopoverContent>
            </Popover>
          ) : null}
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        className="shrink-0 bg-[#00C2C2] hover:bg-[#00A3A3] h-8 text-xs"
        onClick={() => onApply(data.recommended_send_at)}
      >
        Apply
      </Button>
    </div>
  );
}
